import { Injectable } from "@angular/core";
import { LoggerService } from "@core/logging/logger.service";
import { LogContext } from "@core/logging/logger.types";
import { HttpClient, HttpContext } from "@angular/common/http";
import { SESSION_CONSTANTS } from "@core/constants/auth-constants";
import { StorageService } from "../storage/storage.service";
import { environment } from "@environments/environment";
import { UserProfile } from "@core/interface/user-profile";
import { LoginDetail } from "@core/models/login-detail";
import { TokenDetail } from "@core/interface/token-detail";
import { Router } from "@angular/router";
import { SKIP_GLOBAL_ERROR } from "@core/interceptors/http-context-tokens";
import { firstValueFrom, Subject } from "rxjs";

import { jwtDecode } from "jwt-decode";
import { TokenRole } from "@core/interface/token-role";
import { TokenPermission } from "@core/interface/token-permission";
import { TokenCountry } from "@core/interface/token-country";
import { configuration } from "properties/config";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  decodedToken: TokenDetail | null = null;
  private logoutInProgress = false;
  private validationInProgress = false;
  private readonly tokenLoadedSubject = new Subject<TokenDetail | null>();
  public tokenLoaded$ = this.tokenLoadedSubject.asObservable();
  private readonly log: LogContext;

  AUTH_API_URL = environment.apiUrl;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly storageService: StorageService,
    loggerService: LoggerService
  ) {
    this.log = loggerService.createContext("AuthService");
    this.loadDecodedTokenFromStorage();
  }

  setAuthorize(authorized: boolean) {
    this.storageService.storeObject(
      SESSION_CONSTANTS.IS_AUTHORIZED,
      authorized
    );
  }

  /**
   * Load decoded token data from localStorage on app initialization
   */
  private loadDecodedTokenFromStorage(): void {
    try {
      const storedTokenData = this.storageService.getObject(
        SESSION_CONSTANTS.DECODED_TOKEN_DATA,
        configuration.storageTypes.LocalStorage
      );
      if (storedTokenData) {
        this.decodedToken = storedTokenData as TokenDetail;
        // Check if the token is still valid
        if (this.decodedToken?.exp) {
          const currentTime = this.getCurrentTime();
          if (currentTime >= this.decodedToken.exp) {
            // Token expired, clear it
            this.decodedToken = null;
            this.storageService.removeObject(
              SESSION_CONSTANTS.DECODED_TOKEN_DATA
            );
          }
        }
        // Emit the loaded token (could be null if expired)
        this.tokenLoadedSubject.next(this.decodedToken);
      }
    } catch (error) {
      this.log.error("Failed to load decoded token from storage", error);
      this.decodedToken = null;
      this.tokenLoadedSubject.next(null);
    }
  }

  /**
   * Save decoded token data to localStorage for session management
   */
  private saveDecodedTokenToStorage(): void {
    try {
      if (this.decodedToken) {
        this.storageService.storeObject(
          SESSION_CONSTANTS.DECODED_TOKEN_DATA,
          this.decodedToken,
          configuration.storageTypes.LocalStorage
        );
        this.tokenLoadedSubject.next(this.decodedToken);
      }
    } catch (error) {
      this.log.error("Failed to save decoded token to storage", error);
    }
  }

  getAuthorize() {
    return this.storageService.hasObject(SESSION_CONSTANTS.IS_AUTHORIZED)
      ? this.storageService.getObject(SESSION_CONSTANTS.IS_AUTHORIZED)
      : false;
  }

  private getLoginEndpoint(): string {
    return `${this.AUTH_API_URL}/${configuration.authUrls.login}`;
  }

  private getLogoutEndpoint(): string {
    return `${this.AUTH_API_URL}/${configuration.authUrls.logout}`;
  }

  private getRefreshSessionEndpoint(): string {
    return `${this.AUTH_API_URL}/${configuration.authUrls.refreshSession}`;
  }

  private getValidateTokenEndpoint(): string {
    return `${this.AUTH_API_URL}/auth/validate-token`;
  }

  userLogin(code: any) {
    return this.http.post(this.getLoginEndpoint(), { code });
  }

  userRefreshSession() {
    return this.http.get(this.getRefreshSessionEndpoint());
  }

  validateToken() {
    const context = new HttpContext().set(SKIP_GLOBAL_ERROR, true);
    return this.http.get(this.getValidateTokenEndpoint(), { context });
  }

  userLogout() {
    if (this.logoutInProgress) {
      return;
    }

    this.logoutInProgress = true;
    const context = new HttpContext().set(SKIP_GLOBAL_ERROR, true);

    this.http
      .post(this.getLogoutEndpoint(), null, {
        context,
      })
      .subscribe({
        next: (res: any) => {
          this.clearStorage();
          this.logoutInProgress = false;
          this.redirectToURL('login');
        },
        error: (error: any) => {
          this.log.error("Logout request failed", error);
          this.clearStorage();
          this.logoutInProgress = false;
          this.redirectToURL('login');
        },
      });
  }

  decodeToken(token: string): TokenDetail {
    if (!token || token.trim() === "") {
      throw new Error("Cannot decode empty token");
    }
    try {
      return jwtDecode<TokenDetail>(token);
    } catch (error) {
      this.log.error("Failed to decode token", error);
      throw new Error("Invalid token format");
    }
  }

  private getCurrentTime(): number {
    const now = new Date();
    return Math.floor(now.getTime() / 1000);
  }

  /**
   * Checks if the current session is valid
   * Since we're using HTTP-only cookies, we call the validate-token endpoint
   * to validate session state when the decoded token is not available (page refresh)
   * @returns Promise<boolean> indicating if session is valid based on backend validation
   */
  async isSessionValid(): Promise<boolean> {
    if (!this.decodedToken) {
      // If validation is already in progress, return false to prevent infinite loops
      if (this.validationInProgress) {
        return false;
      }

      try {
        this.validationInProgress = true;
        const response: any = await firstValueFrom(this.validateToken());
         if (response?.valid) {
          // Backend may return snake_case (access_token) or camelCase (accessToken)
          const accessToken = response.accessToken ?? response.access_token;
          // Update the decoded token from the response if available
          if (accessToken) {
            this.decodedToken = this.decodeToken(accessToken);
            this.saveDecodedTokenToStorage(); // This will also emit the event 
        } else if (response.sessionInfo) {
            // If backend returns session info instead of token, use that
            this.decodedToken = this.createTokenFromSessionInfo(
              response.sessionInfo
            );
            this.saveDecodedTokenToStorage(); // This will also emit the event
          }
          this.validationInProgress = false;
          return true;
        }
        this.validationInProgress = false;
        return false;
      } catch (error) {
        this.log.error("Token validation failed", error);
        this.validationInProgress = false;
        return false;
      }
    }

    const currentAt = this.getCurrentTime();
    const expiryAt = this.getTokenExpiryTime();

    return !!(expiryAt && currentAt < expiryAt);
  }

  getSessionTimeLeftInSeconds(): number {
    if (!this.decodedToken) {
      return 0;
    }
    const expiryTime = this.getTokenExpiryTime();
    return expiryTime ? expiryTime - this.getCurrentTime() : 0;
  }

  /**
   * Gets total session duration in seconds
   * Calculated from the JWT token's expiration and issue times
   * @returns total session duration in seconds
   */
  getTotalSessionTimeInSeconds(): number {
    if (!this.decodedToken) {
      return 0;
    }
    return this.decodedToken.exp - this.decodedToken.iat;
  }

  /**
   * Create a token-like object from session info returned by validate-token endpoint
   * This is used when the backend returns session info instead of a full JWT token
   */
  private createTokenFromSessionInfo(sessionInfo: any): TokenDetail {
    const currentTime = this.getCurrentTime();
    const defaultSessionDuration = 3600; // 1 hour default

    return {
      sub: sessionInfo.userUUID ?? sessionInfo.sub ?? "",
      exp: sessionInfo.exp ?? currentTime + defaultSessionDuration,
      iat: sessionInfo.iat ?? currentTime,
      typ: sessionInfo.typ ?? "Bearer",
      roleDup: sessionInfo.role ?? { id: 0, name: "" },
      user_id: sessionInfo.userId ?? "",
      user_uuid: sessionInfo.userUUID ?? "",
      first_name: sessionInfo.firstName ?? sessionInfo.first_name ?? "",
      middle_name: sessionInfo.middleName ?? sessionInfo.middle_name ?? "",
      last_name: sessionInfo.lastName ?? sessionInfo.last_name ?? "",
      email: sessionInfo.email ?? "",
      permissionsDup: sessionInfo.permissions ?? [],
      country: sessionInfo.country ?? { id: 0, name: "" },
      region: sessionInfo.region ?? { id: 0, name: "" },
      assigned_countries: sessionInfo.assignedCountries ?? [],
      assigned_regions: sessionInfo.assignedRegions ?? [],
      user_status: sessionInfo.userStatus ?? "Active",
    };
  }

  /**
   * Saves login details from authentication response
   * Note: Cookies are now automatically handled by the browser through HTTP-only cookies
   * We only need to store the user region and decode the JWT token for session management
   */
  saveLoginDetails(loginDetail: LoginDetail): void {
    try {
      if (!loginDetail?.accessToken) {
        throw new Error("Invalid login details: missing access token");
      }

      this.decodedToken = this.decodeToken(loginDetail.accessToken);
      if (loginDetail.userRegion) {
        localStorage.setItem(
          SESSION_CONSTANTS.USER_REGION,
          loginDetail.userRegion
        );
      }
      this.saveDecodedTokenToStorage(); // Save decoded token data for session management
    } catch (error) {
      this.log.error("Failed to save login details", error);
      throw error;
    }
  }

  getUserInjectorDataAccess() {
    return this.storageService.getObject(
      SESSION_CONSTANTS.USER_INJECTOR_DATA_ACCESS
    );
  }

  // Note: getAccessToken() is no longer used since we're using HTTP-only cookies
  // The browser automatically handles cookie-based authentication

  getCsrfToken(): string {
    return "";
  }

  /**
   * Checks if user has a valid session based on decoded token
   * Since cookies are handled automatically by the browser, we rely on the JWT token
   * that was decoded during login to validate session state
   */

  getTokenIssueTime(): number | null {
    return this.decodedToken ? this.decodedToken.iat : null;
  }

  getTokenExpiryTime(): number | null {
    return this.decodedToken ? this.decodedToken.exp : null;
  }

  getCountry(): TokenCountry | null {
    return this.decodedToken ? this.decodedToken?.country : null;
  }

  getRole(): TokenRole | null {
    return this.decodedToken ? this.decodedToken?.roleDup : null;
  }

  getPermissions(): TokenPermission[] | null {
    return this.decodedToken ? this.decodedToken?.permissionsDup : null;
  }

  isUserLoggedIn(): boolean {
    return this.decodedToken !== null;
  }

  getProfile(): UserProfile | null {
    const existingGigyaProfile =
      this.storageService.getObject(SESSION_CONSTANTS.PROFILE) ?? {};
    return this.decodedToken
      ? ({
          ...existingGigyaProfile,
          email: this.decodedToken?.email,
          firstName: existingGigyaProfile?.firstName,
          lastName: existingGigyaProfile?.lastName,
        } as UserProfile)
      : null;
  }

  /**
   * Redirects to the specified path using Angular Router for SPA navigation.
   *
   * Uses replaceUrl so the page being left behind (typically /home on logout or
   * /callback on a failed login) is removed from browser history and cannot be
   * reached with the Back button.
   *
   * @param path - The path to navigate to (e.g., 'login', 'home')
   */
  redirectToURL(path: string): void {
    this.router.navigate([`/${path}`], { replaceUrl: true });
  }

  clearStorage(): void {
    try {
      this.storageService.clearAllObjects();
      this.decodedToken = null; // Clear in-memory token
      this.tokenLoadedSubject.next(null); // Notify subscribers that token is cleared
      this.logoutInProgress = false; // Reset logout flag when clearing storage
      this.validationInProgress = false; // Reset validation flag when clearing storage
    } catch (error) {
      this.log.error("Failed to clear storage", error);
      this.decodedToken = null; // Still clear token even on error
      this.tokenLoadedSubject.next(null);
      this.logoutInProgress = false;
      this.validationInProgress = false;
    }
  }
}
